import { useReducer, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCenterTheme } from '@hooks/usePublicBooking';
import { widgetReducer, initialWidgetState } from './widgetState';
import { StepIndicator } from './StepIndicator';
import { ServiceStep } from './ServiceStep';
import { StaffStep } from './StaffStep';
import { DateTimeStep } from './DateTimeStep';
import { DetailsStep } from './DetailsStep';
import { ConfirmationStep } from './ConfirmationStep';
import { PageLoading } from '@components/common/LoadingSpinner';

const SESSION_KEY = 'widget_booking_state';

export function BookingWidget() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const centerSlug = slug ?? '';

  const { data: theme, isLoading: themeLoading, isError: themeError } = useCenterTheme(centerSlug);

  // Restore flow from sessionStorage (cookieless)
  const [state, dispatch] = useReducer(widgetReducer, initialWidgetState, (init) => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      return saved ? (JSON.parse(saved) as typeof init) : init;
    } catch {
      return init;
    }
  });

  // Persist flow to sessionStorage
  useEffect(() => {
    if (state.step !== 'confirmation') {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
      } catch {
        // ignore
      }
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [state]);

  // Apply center theme as CSS variables
  useEffect(() => {
    if (!theme) return;
    document.documentElement.style.setProperty('--widget-primary', theme.primaryColor);
    if (theme.secondaryColor) {
      document.documentElement.style.setProperty('--widget-secondary', theme.secondaryColor);
    }
  }, [theme]);

  if (!centerSlug) {
    return <p className="p-4 text-destructive">Center not specified.</p>;
  }

  if (themeLoading) return <PageLoading />;

  // Centre unknown OR disabled by admin — backend returns the same 404
  // (anti-enumeration). Either way, the customer sees a friendly "not
  // available" panel instead of a half-loaded wizard that breaks at the
  // availability step.
  if (themeError || !theme) {
    return (
      <div
        className="widget-root widget-container mx-auto max-w-lg rounded-lg border bg-card p-8 text-center shadow-sm"
        data-testid="booking-widget-unavailable"
      >
        <h1 className="mb-2 text-lg font-semibold">{t('widget.unavailable.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('widget.unavailable.body')}</p>
      </div>
    );
  }

  return (
    <div
      className="widget-root widget-container mx-auto max-w-lg rounded-lg border bg-card p-6 shadow-sm"
      data-testid="booking-widget"
    >
      {/* Center branding */}
      {theme.logoUrl && (
        <img
          src={theme.logoUrl}
          alt={theme.name}
          className="mb-4 h-10 object-contain"
        />
      )}

      {state.step !== 'confirmation' && <StepIndicator currentStep={state.step} />}

      {/* Slot taken error */}
      {state.error === 'slot_taken' && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
          data-testid="slot-taken-error"
        >
          {t('booking.slotTaken')}
        </div>
      )}

      {/* Step rendering */}
      {state.step === 'service' && (
        <ServiceStep
          centerSlug={centerSlug}
          currency={theme.currency ?? 'EUR'}
          onSelect={(service) => { dispatch({ type: 'SELECT_SERVICE', service }); }}
        />
      )}

      {state.step === 'staff' && state.selectedService && (
        <StaffStep
          centerSlug={centerSlug}
          serviceId={state.selectedService.id}
          onSelect={(staff) => { dispatch({ type: 'SELECT_STAFF', staff }); }}
          onSkip={() => { dispatch({ type: 'SKIP_STAFF' }); }}
          onBack={() => { dispatch({ type: 'GO_BACK' }); }}
        />
      )}

      {state.step === 'datetime' && state.selectedService && (
        <DateTimeStep
          centerSlug={centerSlug}
          serviceId={state.selectedService.id}
          staffId={state.selectedStaff?.id}
          centerTimezone={theme.timezone ?? 'Europe/Madrid'}
          selectedDate={state.selectedDate}
          onSelectDate={(date) => { dispatch({ type: 'SELECT_DATE', date }); }}
          onSelectSlot={(slot) => { dispatch({ type: 'SELECT_SLOT', slot }); }}
          onBack={() => { dispatch({ type: 'GO_BACK' }); }}
        />
      )}

      {state.step === 'details' && (
        <DetailsStep
          widgetState={state}
          centerSlug={centerSlug}
          centerTimezone={theme.timezone ?? 'Europe/Madrid'}
          onConfirmed={(id, token, deadline) => {
            dispatch({
              type: 'BOOKING_CONFIRMED',
              id,
              trackingToken: token,
              confirmationDeadline: deadline,
            });
          }}
          onSlotTaken={() => { dispatch({ type: 'SET_ERROR', error: 'slot_taken' }); }}
          onBack={() => { dispatch({ type: 'GO_BACK' }); }}
        />
      )}

      {state.step === 'confirmation' &&
        state.confirmationId &&
        state.confirmationTrackingToken && (
          <ConfirmationStep
            bookingId={state.confirmationId}
            trackingToken={state.confirmationTrackingToken}
            confirmationDeadline={state.confirmationDeadline}
            onReset={() => { dispatch({ type: 'RESET' }); }}
          />
        )}
    </div>
  );
}
